import React from 'react';
import { CurrencyFormatter, parseAndFormatRupiah, parseAndFormatRupiahCompact } from '@/lib/currency';

interface CurrencyProps {
  amount: number | string;
  format?: 'standard' | 'compact' | 'numberOnly' | 'withDecimals';
  className?: string;
  showZero?: boolean;
}

/**
 * Currency component for consistent Rupiah formatting
 * @param amount - The amount to display (number or string)
 * @param format - The format type (default: 'standard')
 * @param className - Additional CSS classes
 * @param showZero - Whether to show zero values (default: true)
 */
export function Currency({ 
  amount, 
  format = 'standard', 
  className = '', 
  showZero = true 
}: CurrencyProps) {
  // Convert string to number if needed
  let numericAmount: number;
  
  if (typeof amount === 'string') {
    // Remove all non-digit characters except decimal point
    const cleanedInput = amount.replace(/[^\d.,]/g, '');
    // Handle Indonesian decimal format (comma as decimal separator)
    const normalizedInput = cleanedInput.replace(',', '.');
    numericAmount = parseFloat(normalizedInput);
    
    if (isNaN(numericAmount)) {
      numericAmount = 0;
    }
  } else {
    numericAmount = amount;
  }

  // Don't render if amount is 0 and showZero is false
  if (numericAmount === 0 && !showZero) {
    return null;
  }

  // Handle invalid numbers
  if (typeof numericAmount !== 'number' || isNaN(numericAmount)) {
    return <span className={className}>Rp 0</span>;
  }

  let formattedAmount: string;
  
  // Use specialized parsing functions for string inputs in standard and compact formats
  if (typeof amount === 'string' && (format === 'standard' || format === 'compact')) {
    if (format === 'compact') {
      formattedAmount = parseAndFormatRupiahCompact(amount);
    } else {
      formattedAmount = parseAndFormatRupiah(amount);
    }
  } else {
    formattedAmount = CurrencyFormatter[format](numericAmount);
  }

  return (
    <span className={className} title={CurrencyFormatter.standard(numericAmount)}>
      {formattedAmount}
    </span>
  );
}

/**
 * Hook for currency formatting
 */
export function useCurrency() {
  return {
    format: CurrencyFormatter,
    formatRupiah: (amount: number | string, format: 'standard' | 'compact' | 'numberOnly' | 'withDecimals' = 'standard') => {
      if (typeof amount === 'string') {
        if (format === 'compact') {
          return parseAndFormatRupiahCompact(amount);
        } else if (format === 'standard') {
          return parseAndFormatRupiah(amount);
        }
        // For other formats, convert to number first
        const cleanedInput = amount.replace(/[^\d.,]/g, '');
        const normalizedInput = cleanedInput.replace(',', '.');
        const numericValue = parseFloat(normalizedInput);
        if (isNaN(numericValue)) {
          return 'Rp 0';
        }
        return CurrencyFormatter[format](numericValue);
      } else {
        return CurrencyFormatter[format](amount);
      }
    },
    parseAndFormat: parseAndFormatRupiah,
    parseAndFormatCompact: parseAndFormatRupiahCompact,
  };
}